import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, ArrowLeft, Save, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    low_score_threshold: 70,
    email_alerts: false,
    in_app_alerts: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          low_score_threshold: data.low_score_threshold || 70,
          email_alerts: data.email_alerts || false,
          in_app_alerts: data.in_app_alerts !== false
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setMessage('Settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Bell size={24} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Notification Settings</h1>
              <p className="text-slate-600">Configure your alert preferences</p>
            </div>
          </div>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('success') 
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {/* Settings Card */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Low Score Threshold */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Low Score Alert Threshold</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Receive an alert when a call assessment score falls below this threshold.
                </p>
                <div className="mt-4 flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.low_score_threshold}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      low_score_threshold: parseInt(e.target.value) || 0
                    }))}
                    className="w-24"
                    data-testid="threshold-input"
                  />
                  <span className="text-slate-600">%</span>
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500"
                      style={{ width: `${settings.low_score_threshold}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Scores below {settings.low_score_threshold}% will trigger an alert
                </p>
              </div>
            </div>
          </div>

          {/* In-App Alerts */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Bell size={20} className="text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">In-App Notifications</h3>
                  <p className="text-sm text-slate-600">
                    Show notifications in the app when alerts are triggered
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.in_app_alerts}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    in_app_alerts: e.target.checked
                  }))}
                  className="sr-only peer"
                  data-testid="in-app-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>
          </div>

          {/* Email Alerts - Coming Soon */}
          <div className="p-6 bg-slate-50">
            <div className="flex items-center justify-between opacity-60">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-slate-200 rounded-lg">
                  <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email Notifications</h3>
                  <p className="text-sm text-slate-600">
                    Receive alerts via email
                  </p>
                </div>
              </div>
              <span className="text-xs bg-slate-300 text-slate-600 px-2 py-1 rounded-full font-medium">
                Coming Soon
              </span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>

        {/* Info Card */}
        <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">How alerts work</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Alerts are triggered automatically when a call assessment is completed</li>
            <li>• All admins receive alerts for scores below the threshold</li>
            <li>• Click on a notification to view the assessment details</li>
            <li>• Notifications are checked every 30 seconds for real-time updates</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default NotificationSettings;
